/**
 * Standard form validation UI for this project.
 *
 * Use together with `noValidate` on `<form>` and your own validate() — do not rely on
 * native HTML `required` tooltips. Per-field: `InlineFieldError` under the control; for
 * submit/API failures: `FormSummaryMessage`. Match invalid controls with
 * `border-red-300 bg-red-50/35` (or equivalent) + `aria-invalid` / `aria-describedby`.
 */

type InlineFieldErrorProps = {
  id?: string;
  message: string | undefined;
};

export function InlineFieldError({ id, message }: InlineFieldErrorProps) {
  if (!message) return null;
  return (
    <p
      id={id}
      className="mt-1.5 text-xs text-red-600 leading-relaxed flex gap-1.5 items-start"
      role="alert"
    >
      <i className="ri-error-warning-line text-sm shrink-0 opacity-90 mt-px" aria-hidden />
      <span>{message}</span>
    </p>
  );
}

type FormSummaryMessageProps = {
  message: string | undefined;
  variant?: 'error' | 'info';
};

/** Auth / server errors: calm row with icon — not a floating browser “tag”. */
export function FormSummaryMessage({ message, variant = 'error' }: FormSummaryMessageProps) {
  if (!message) return null;
  const isError = variant === 'error';
  return (
    <div
      className={
        isError
          ? 'flex gap-2.5 items-start rounded-lg bg-red-50/90 px-3.5 py-3 text-sm text-red-800 ring-1 ring-red-100/80'
          : 'flex gap-2.5 items-start rounded-lg bg-gray-50 px-3.5 py-3 text-sm text-gray-700 ring-1 ring-gray-100'
      }
      role="alert"
    >
      <i
        className={
          isError
            ? 'ri-error-warning-line text-lg text-red-500 shrink-0 leading-none mt-0.5'
            : 'ri-information-line text-lg text-gray-500 shrink-0 leading-none mt-0.5'
        }
        aria-hidden
      />
      <p className="leading-relaxed min-w-0">{message}</p>
    </div>
  );
}
